import { useState } from "react";
import { useSessionStore, type SessionExercise, type SessionSet } from "../state/useSessionStore";
import { EXERCISE_BY_ID } from "../data/exercises";
import { formatKg, formatRestSeconds } from "../lib/format";
import { weightScopeLabel, rirShortLabel, RIR_OPTIONS, RIR_PROMPT_QUESTION } from "../lib/copy";
import { Card, RiskTag, Sheet } from "./ui";

export function ExerciseCard({
  item,
  isActive = false,
  onReplace,
}: {
  item: SessionExercise;
  isActive?: boolean;
  onReplace?: (exerciseId: string) => void;
}) {
  const ex = EXERCISE_BY_ID.get(item.exerciseId);
  const [showRirSheet, setShowRirSheet] = useState<number | null>(null); // setIndex
  const [showMenu, setShowMenu] = useState(false);
  const [skipReasonModal, setSkipReasonModal] = useState(false);
  const store = useSessionStore();

  if (!ex) return null;

  const completedCount = item.sets.filter((s) => s.completed).length;
  const isAllDone = completedCount >= item.targetSets;
  const isSkipped = item.status === "skipped";
  const weightStep = ex.incrementType === "per_dumbbell" || ex.incrementType === "barbell_total" ? 2.5 : 5;

  // 下一个待完成的组
  const nextPendingSet = item.sets.find((s) => !s.completed);

  const handleToggleComplete = (set: SessionSet) => {
    if (set.completed) {
      store.updateSet(item.exerciseId, set.setIndex, { completed: false, completedAt: null });
    } else {
      store.completeSet(item.exerciseId, set.setIndex);
    }
  };

  const handleSkipExercise = (reason: string) => {
    store.markSkipped(item.exerciseId, reason);
    setSkipReasonModal(false);
    setShowMenu(false);
  };

  return (
    <Card
      className={`mb-4 overflow-hidden transition-all duration-200 border ${
        isSkipped
          ? "opacity-60 bg-black/[0.02] dark:bg-white/[0.02] border-dashed border-tone-border"
          : isAllDone
            ? "border-tone-good/30 bg-tone-good/[0.02]"
            : isActive
              ? "border-tone-accent ring-1 ring-tone-accent/30 shadow-sm"
              : "border-tone-border"
      }`}
    >
      {/* 替换动作撤销条 */}
      {item.replacedOriginal && (
        <div className="bg-tone-accentSoft/70 border-b border-tone-border px-4 py-2 flex items-center justify-between text-[12px] text-tone-ink font-medium">
          <span>
            已替换原动作「{EXERCISE_BY_ID.get(item.replacedOriginal.exerciseId)?.nameZh ?? "原动作"}」
          </span>
          <button
            type="button"
            onClick={() => store.undoReplaceExercise(item.exerciseId)}
            className="text-tone-accent font-bold hover:underline active:scale-95 ml-2"
          >
            撤销替换
          </button>
        </div>
      )}

      {/* 头部信息 */}
      <div className="p-4 pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-[17px] font-bold text-tone-ink tracking-tight font-headline flex items-center gap-1.5">
                <span>{ex.nameZh}</span>
                {isAllDone && (
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-tone-good text-white text-[10px] font-bold">
                    ✓
                  </span>
                )}
                {isSkipped && (
                  <span className="text-[11px] font-normal text-tone-soft px-1.5 py-0.5 rounded bg-black/5 dark:bg-white/10">
                    已跳过
                  </span>
                )}
              </h3>
            </div>

            {/* 动作规格与口径 */}
            <div className="text-[12px] font-medium text-tone-soft flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 font-caption">
              <span>{ex.primaryMuscles.join(" · ")}</span>
              <span>•</span>
              <span>目标 {item.repMin}–{item.repMax} 次</span>
              <span>•</span>
              <span>休息 {formatRestSeconds(item.restSeconds)}</span>
              <span>•</span>
              <span className="text-tone-ink font-semibold">{weightScopeLabel(item.weightUnitScope)}</span>
            </div>
          </div>

          {/* 状态与更多操作菜单 */}
          <div className="flex items-center gap-1.5">
            {/* 极简圆点进度 */}
            <div className="flex items-center gap-1 mr-1">
              {item.sets.map((s) => (
                <span
                  key={s.setIndex}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    s.completed
                      ? "bg-tone-good scale-110"
                      : "bg-black/15 dark:bg-white/20"
                  }`}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => setShowMenu(!showMenu)}
              className="w-8 h-8 rounded-elem text-tone-soft hover:text-tone-ink hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-center text-base active:scale-95"
              aria-label="更多操作"
            >
              ⋯
            </button>
          </div>
        </div>

        {/* 更多操作下拉菜单 */}
        {showMenu && (
          <div className="mt-2 p-2 rounded-elem bg-tone-card border border-tone-border shadow-lg flex flex-wrap gap-2 animate-fade-in">
            {onReplace && (
              <button
                type="button"
                onClick={() => {
                  setShowMenu(false);
                  onReplace(item.exerciseId);
                }}
                className="text-[12px] font-semibold text-tone-accent px-3 py-2 rounded-elem bg-tone-accentSoft active:scale-95"
              >
                替换此动作
              </button>
            )}
            {!isSkipped && (
              <button
                type="button"
                onClick={() => {
                  setShowMenu(false);
                  setSkipReasonModal(true);
                }}
                className="text-[12px] font-semibold text-tone-warn px-3 py-2 rounded-elem bg-tone-warn/10 active:scale-95"
              >
                跳过此动作…
              </button>
            )}
          </div>
        )}

        {ex.lockedUntilMedicalClearance && (
          <div className="mt-2">
            <RiskTag tone="danger">⚠️ 医疗保护动作（需许可）</RiskTag>
          </div>
        )}
      </div>

      {/* 核心组列表（盲操微调与大触控） */}
      {!isSkipped && (
        <div className="px-3 pb-3">
          <div className="rounded-elem bg-black/[0.02] dark:bg-white/[0.03] p-2 space-y-2">
            {/* 表头 */}
            <div className="grid grid-cols-[32px_1fr_1fr_48px_48px] gap-1 px-1 py-0.5 text-[11px] font-bold text-tone-soft text-center select-none font-caption">
              <span>组</span>
              <span>重量</span>
              <span>次数</span>
              <span>余力</span>
              <span>完成</span>
            </div>

            {/* 组行列表 */}
            {item.sets.map((set) => {
              const currentWeight = set.weightKg ?? item.suggestedWeightKg ?? 0;
              const currentReps = set.reps ?? item.repMin ?? 8;
              const isCurrentNext = nextPendingSet?.setIndex === set.setIndex;

              return (
                <div
                  key={set.id || set.setIndex}
                  className={`grid grid-cols-[32px_1fr_1fr_48px_48px] gap-1 items-center px-1.5 py-2 rounded-elem transition-all duration-150 ${
                    set.completed
                      ? "bg-tone-good/10 text-tone-ink font-semibold"
                      : isCurrentNext
                        ? "bg-tone-card border border-tone-accent/40 shadow-sm"
                        : "bg-tone-card border border-tone-border/60"
                  }`}
                >
                  {/* 组号 */}
                  <div className="flex items-center justify-center">
                    <span
                      className={`w-6 h-6 rounded-full text-[12px] font-bold flex items-center justify-center tabular-nums font-display ${
                        set.completed
                          ? "bg-tone-good text-white"
                          : isCurrentNext
                            ? "bg-tone-accent text-white"
                            : "bg-black/5 dark:bg-white/10 text-tone-soft"
                      }`}
                    >
                      {set.setIndex}
                    </span>
                  </div>

                  {/* 重量盲操微调 (≥32px 大字) */}
                  <div className="flex items-center justify-center gap-0.5">
                    <button
                      type="button"
                      disabled={set.completed}
                      className="w-7 h-7 rounded-elem bg-black/5 dark:bg-white/10 text-sm font-bold active:scale-90 disabled:opacity-30 flex items-center justify-center"
                      onClick={() =>
                        store.updateSet(item.exerciseId, set.setIndex, {
                          weightKg: Math.max(0, currentWeight - weightStep),
                        })
                      }
                      aria-label="减重量"
                    >
                      −
                    </button>
                    <span className="text-[16px] sm:text-[18px] font-bold tabular-nums w-12 text-center text-tone-ink font-display">
                      {formatKg(currentWeight)}
                    </span>
                    <button
                      type="button"
                      disabled={set.completed}
                      className="w-7 h-7 rounded-elem bg-black/5 dark:bg-white/10 text-sm font-bold active:scale-90 disabled:opacity-30 flex items-center justify-center"
                      onClick={() =>
                        store.updateSet(item.exerciseId, set.setIndex, {
                          weightKg: currentWeight + weightStep,
                        })
                      }
                      aria-label="加重量"
                    >
                      +
                    </button>
                  </div>

                  {/* 次数盲操微调 */}
                  <div className="flex items-center justify-center gap-0.5">
                    <button
                      type="button"
                      disabled={set.completed}
                      className="w-7 h-7 rounded-elem bg-black/5 dark:bg-white/10 text-sm font-bold active:scale-90 disabled:opacity-30 flex items-center justify-center"
                      onClick={() =>
                        store.updateSet(item.exerciseId, set.setIndex, {
                          reps: Math.max(1, currentReps - 1),
                        })
                      }
                      aria-label="减次数"
                    >
                      −
                    </button>
                    <span className="text-[16px] sm:text-[18px] font-bold tabular-nums w-8 text-center text-tone-ink font-display">
                      {currentReps}
                    </span>
                    <button
                      type="button"
                      disabled={set.completed}
                      className="w-7 h-7 rounded-elem bg-black/5 dark:bg-white/10 text-sm font-bold active:scale-90 disabled:opacity-30 flex items-center justify-center"
                      onClick={() =>
                        store.updateSet(item.exerciseId, set.setIndex, {
                          reps: currentReps + 1,
                        })
                      }
                      aria-label="加次数"
                    >
                      +
                    </button>
                  </div>

                  {/* 余力选择触发器 */}
                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      disabled={set.completed}
                      onClick={() => setShowRirSheet(set.setIndex)}
                      className="min-h-[36px] px-1.5 rounded-elem text-[11px] font-bold text-tone-soft hover:text-tone-ink bg-black/5 dark:bg-white/10 flex items-center justify-center font-caption"
                    >
                      {rirShortLabel(set.rir ?? item.targetRir)}
                    </button>
                  </div>

                  {/* 核心完成打勾按钮（≥48×48px 触控区） */}
                  <div className="flex items-center justify-center">
                    <button
                      type="button"
                      onClick={() => handleToggleComplete(set)}
                      className={`w-[44px] h-[44px] rounded-elem font-bold flex items-center justify-center transition-all duration-150 active:scale-90 shadow-sm ${
                        set.completed
                          ? "bg-tone-good text-white"
                          : isCurrentNext
                            ? "bg-tone-accent text-white shadow-tone-accent/20 animate-pulse"
                            : "bg-black/5 dark:bg-white/15 text-tone-soft hover:bg-tone-good/20 hover:text-tone-good"
                      }`}
                      aria-label={`完成第 ${set.setIndex} 组`}
                    >
                      <span className="text-lg leading-none">✓</span>
                    </button>
                  </div>
                </div>
              );
            })}

            {/* 加一组 */}
            <div className="flex items-center justify-between pt-1 px-1">
              <button
                type="button"
                onClick={() => store.addSet(item.exerciseId)}
                className="text-[12px] font-bold text-tone-accent hover:text-tone-accent/80 flex items-center gap-1 py-1.5 px-3 rounded-elem bg-tone-accentSoft/60 active:scale-95 transition-all"
              >
                <span>+ 加一组</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 余力 (RIR) 快速选择抽屉 */}
      {showRirSheet !== null && (
        <Sheet
          open={showRirSheet !== null}
          onClose={() => setShowRirSheet(null)}
          title={`第 ${showRirSheet} 组 · ${RIR_PROMPT_QUESTION}`}
        >
          <div className="space-y-2.5">
            <p className="text-[13px] text-tone-soft mb-3 font-caption">
              请根据做完该组时的真实体感选择剩余可做次数，算法据此评估是否加重或维持：
            </p>
            {RIR_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => {
                  store.updateSet(item.exerciseId, showRirSheet, {
                    rir: opt.value,
                    reachedFailure: opt.value === 0,
                  });
                  setShowRirSheet(null);
                }}
                className="w-full min-h-[52px] px-4 py-3 rounded-elem bg-tone-card border border-tone-border flex items-center justify-between active:scale-[0.99] transition-all hover:border-tone-accent text-left"
              >
                <div>
                  <div className="text-[15px] font-bold text-tone-ink font-headline">{opt.label}</div>
                  <div className="text-[12px] text-tone-soft mt-0.5 font-caption">{opt.description}</div>
                </div>
                <span className="text-tone-accent font-bold text-lg">→</span>
              </button>
            ))}

            {/* 本组动作形变/借力开关 */}
            {(() => {
              const currentSet = item.sets.find((s) => s.setIndex === showRirSheet);
              return (
                <div className="pt-2 border-t border-tone-border mt-3">
                  <label className="flex items-center justify-between p-3 rounded-elem bg-black/[0.03] dark:bg-white/[0.04] cursor-pointer">
                    <div>
                      <div className="text-[13px] font-bold text-tone-ink">本组动作失控 / 明显借力</div>
                      <div className="text-[11px] text-tone-soft">出现形变、借力或控制不稳（规则将建议维持重量巩固）</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={currentSet?.formBreakdown ?? false}
                      onChange={(e) => {
                        store.updateSet(item.exerciseId, showRirSheet, {
                          formBreakdown: e.target.checked,
                        });
                      }}
                      className="w-5 h-5 accent-tone-accent rounded"
                    />
                  </label>
                </div>
              );
            })()}
          </div>
        </Sheet>
      )}

      {/* 跳过动作理由确认弹窗 */}
      {skipReasonModal && (
        <Sheet
          open={skipReasonModal}
          onClose={() => setSkipReasonModal(false)}
          title={`跳过动作 · ${ex.nameZh}`}
        >
          <div className="space-y-2.5">
            <p className="text-[13px] text-tone-soft mb-3 font-caption">
              请选择跳过原因，系统将记录并在进阶评估中做出合理解释（不扣减基础表现）：
            </p>
            {[
              { id: "equipment_busy", label: "器械被占用 / 没排上" },
              { id: "joint_discomfort", label: "关节或足部略有不适" },
              { id: "lack_of_time", label: "时间不够，优先完成核心组" },
              { id: "excessive_fatigue", label: "今日体力已耗尽" },
            ].map((r) => (
              <button
                key={r.id}
                onClick={() => handleSkipExercise(r.id)}
                className="w-full min-h-[48px] px-4 py-3 rounded-elem bg-tone-card border border-tone-border flex items-center justify-between active:scale-[0.99] transition-all hover:border-tone-warn text-left"
              >
                <span className="text-[14px] font-semibold text-tone-ink">{r.label}</span>
                <span className="text-tone-soft text-sm">选择</span>
              </button>
            ))}
          </div>
        </Sheet>
      )}
    </Card>
  );
}
